import { Box3, BoxGeometry, Color, ConeGeometry, Float32BufferAttribute, Group, InstancedMesh, Matrix4, MeshLambertMaterial, type BufferGeometry } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { columnPieces } from '../../core/columns';
import type { LevelRun } from '../../core/level-runner';
import { WORLD_TUNING } from '../../data/tuning';
import { InstancedBatch } from '../pools/instanced-batch';
import { columnPieceParts, partsCoverage } from './world-1';

/** One coloured geometry and one draw call per skin; y is scaled to each collision piece (#70). */
export function buildColumnBatches(run: LevelRun, depth: number, fallbackColor: number) {
  const group = new Group();
  group.name = 'columns';
  const halfW = WORLD_TUNING[run.level.def.world].columnWidth / 2;
  const batches = new Map<string, InstancedBatch>();
  const meshes: InstancedMesh[] = [];
  const geometries: BufferGeometry[] = [];
  const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true });
  // Carry self-lit signs in the same batch without lighting the opaque column body.
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = 'attribute vec3 skinEmissive;\nvarying vec3 vSkinEmissive;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvSkinEmissive = skinEmissive;');
    shader.fragmentShader = 'varying vec3 vSkinEmissive;\n' + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vSkinEmissive;');
  };
  material.customProgramCacheKey = () => 'instanced-column-emissive-v1';
  const coverage = new Map<string, number>();
  const boxes = new Map<string, { min: [number, number, number]; max: [number, number, number] }[]>();
  const pieces = run.level.columns.map((column) => columnPieces(column, halfW * 2));
  // Cali stacks fixed 1 u modules: bake each distinct piece height instead of stretching its floors.
  const exactHeight = run.level.def.world === 2;
  const keyFor = (skin: string, halfH: number) => exactHeight ? `${skin}:${halfH}` : skin;
  const variants = new Map<string, { skin: string; halfH: number; index: number; side: 0 | 1; capacity: number }>();
  run.level.columns.forEach((column, index) => {
    for (const side of [0, 1] as const) {
      const halfH = pieces[index]![side].halfH;
      const key = keyFor(column.skin, halfH);
      const variant = variants.get(key);
      if (variant) variant.capacity += 1;
      else variants.set(key, { skin: column.skin, halfH: exactHeight ? halfH : 1, index, side, capacity: 1 });
    }
  });
  const templates = new Map<string, ReturnType<typeof columnPieceParts>>();
  const color = new Color();

  for (const [key, variant] of variants) {
    const { skin, halfH, capacity } = variant;
    const parts = columnPieceParts(skin, halfW, halfH, depth, fallbackColor);
    templates.set(key, parts);
    const sources = parts.map((part) => {
      const geometry = (part.shape === 'cone' ? new ConeGeometry(part.w / 2, part.h, 4) : new BoxGeometry(part.w, part.h, part.d)).toNonIndexed();
      geometry.translate(part.x, part.y, part.z);
      color.setHex(part.color);
      const colors = new Float32Array(geometry.getAttribute('position').count * 3);
      for (let vertex = 0; vertex < colors.length; vertex += 3) {
        colors[vertex] = color.r;
        colors[vertex + 1] = color.g;
        colors[vertex + 2] = color.b;
      }
      geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
      geometry.setAttribute('skinEmissive', new Float32BufferAttribute(part.emissive ? colors.slice() : new Float32Array(colors.length), 3));
      return geometry;
    });
    const geometry = mergeGeometries(sources)!;
    sources.forEach((source) => source.dispose());
    geometry.computeBoundingBox();
    geometries.push(geometry);
    const mesh = new InstancedMesh(geometry, material, capacity);
    mesh.name = exactHeight ? `columns.${skin}.${halfH}.instanced` : `columns.${skin}.instanced`;
    mesh.frustumCulled = false;
    const batch = new InstancedBatch(mesh, capacity);
    mesh.userData.instanceIds = batch.names;
    batches.set(key, batch);
    meshes.push(mesh);
    group.add(mesh);
  }

  run.level.columns.forEach((column, index) => {
    for (const [piece, side] of [[pieces[index]![0], 'bottom'], [pieces[index]![1], 'top']] as const) {
      const parts = templates.get(keyFor(column.skin, piece.halfH))!;
      const scaled = exactHeight ? parts : parts.map((part) => ({ ...part, y: part.y * piece.halfH, h: part.h * piece.halfH }));
      const name = `column:${index}:${side}`;
      coverage.set(name, partsCoverage(scaled, piece.halfW, piece.halfH));
      boxes.set(name, scaled.map((part) => ({
        min: [piece.x + part.x - part.w / 2, piece.y + part.y - part.h / 2, part.z - part.d / 2],
        max: [piece.x + part.x + part.w / 2, piece.y + part.y + part.h / 2, part.z + part.d / 2],
      })));
    }
  });
  const matrix = new Matrix4();
  const bounds = new Box3();
  const checked = new Set<string>();
  const failures = new Set<string>();
  const active = new Set<number>();
  const update = () => {
    active.clear();
    for (const entity of run.entities) if (entity.kind === 'column' && entity.active) active.add(entity.index);
    for (const batch of batches.values()) {
      for (let slot = batch.names.length - 1; slot >= 0; slot--) {
        const name = batch.names[slot]!;
        if (!active.has(Number(name.split(':')[1]))) batch.release(name);
      }
    }
    for (const index of active) {
      const column = run.level.columns[index]!;
      for (const [piece, side] of [[pieces[index]![0], 'bottom'], [pieces[index]![1], 'top']] as const) {
        const batch = batches.get(keyFor(column.skin, piece.halfH))!;
        matrix.makeScale(1, exactHeight ? 1 : piece.halfH, 1);
        matrix.setPosition(piece.x, piece.y, 0);
        const name = `column:${index}:${side}`;
        batch.set(name, matrix);
        if (import.meta.env.DEV) {
          const mesh = batch.mesh as InstancedMesh;
          mesh.getMatrixAt(batch.indexOf(name)!, matrix);
          bounds.copy(mesh.geometry.boundingBox!).applyMatrix4(matrix);
          checked.add(name);
          if (Math.abs(bounds.min.x - (piece.x - piece.halfW)) > 0.02 || Math.abs(bounds.max.x - (piece.x + piece.halfW)) > 0.02 || Math.abs(bounds.min.y - (piece.y - piece.halfH)) > 0.02 || Math.abs(bounds.max.y - (piece.y + piece.halfH)) > 0.02) failures.add(name);
        }
      }
    }
  };
  update();
  return {
    group, update,
    validation: () => ({ checked: checked.size, failures: [...failures] }),
    warmup() {
      // Make each skin upload its geometry before flight, even if its first column is far ahead.
      for (const [key, batch] of batches) {
        const { index, side } = variants.get(key)!;
        const piece = pieces[index]![side];
        matrix.makeScale(1, exactHeight ? 1 : piece.halfH, 1);
        matrix.setPosition(piece.x, piece.y, 0);
        batch.set(`column:${index}:${side === 0 ? 'bottom' : 'top'}`, matrix);
      }
    },
    coverage: (name: string) => coverage.get(name) ?? 0,
    parts: (name: string) => boxes.get(name) ?? [],
    dispose() {
      meshes.forEach((mesh) => mesh.dispose());
      geometries.forEach((geometry) => geometry.dispose());
      material.dispose();
    },
  };
}
